import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { COLORS } from "../../lib/options";
import { InsuranceCodeField, OdometerField, StateSelect, TagField, TextField } from "../../components/fields";
import { useUnsavedGuard, clearUnsavedWork } from "../../lib/unsaved";
import { recordTrade } from "./tradeIn";
import { Button, Field, MoneyField, OptionSelect, Select, ErrorNote, Empty, Panel,
} from "../../components/ui";
import { formatMoney, parseMoney, centsToInput } from "../../lib/money";
import { computeDeal, type DealAmounts } from "../../lib/tax";
import { loadCounties, bpsToPercent, surtaxLabel, type County } from "../../lib/counties";
import { customerName } from "../people/CustomerList";
import SearchSelect, { type Choice } from "../../components/SearchSelect";
import { SaveBar } from "../../components/save";
import { mustSave, SaveFailure, useSaveState } from "../../lib/saving";
import { WARRANTY_TERMS, termFor } from "../../lib/warranty";
import AddVehicle from "../lot/AddVehicle";
import AddCustomer from "../people/AddCustomer";

interface VehicleOpt {
  id: string; stock_no: string; year: number; make: string; model: string;
  trim_level: string | null; odometer: number; asking_price_cents: number;
  /* Searched but not shown, so a car can be found by the last of its VIN. */
  vin: string;
}
interface CustomerOpt {
  id: string; first_name: string; middle_name: string | null;
  last_name: string; county: string | null; state: string;
  /* Searched but not shown. Two people with the same name are told apart by
     the phone number a lot faster than by anything else. */
  phone_cell: string | null; email: string | null;
  /* A business is the owner in its own right; the person on the record is
     the officer who signs. There is no second buyer to ask about. */
  is_business: boolean | null;
}

const money = (s: string) => parseMoney(s) ?? 0;

export default function NewDeal({ onDone, onCancel }: {
  onDone: (dealId: string) => void; onCancel: () => void;
}) {
  const [vehicles, setVehicles] = useState<VehicleOpt[]>([]);
  const [customers, setCustomers] = useState<CustomerOpt[]>([]);
  const [counties, setCounties] = useState<County[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  /* Saving…, then a "Deal saved ✓" toast as the deal opens, or why not —
     with every figure still in the form. */
  const save_ = useSaveState();

  /* Creating a car or a buyer from inside the deal, rather than making
     someone abandon a half-written deal to go and add one. */
  const [making, setMaking] = useState<null | "vehicle" | "customer">(null);
  const [vehicleId, setVehicleId] = useState("");
  const [buyerId, setBuyerId] = useState("");
  /* The second name on the title, and how the two are joined — AND needs both
     signatures to sell the car afterwards, OR needs either. */
  const [coBuyerId, setCoBuyerId] = useState("");
  const [joint, setJoint] = useState<"and" | "or">("and");
  const [county, setCounty] = useState("");
  /* Every deal is one or the other, and they cost different amounts. The fee
     figures were already here; nothing recorded which one applied, so the
     total was whatever the registration box happened to hold. */
  const [tagChoice, setTagChoice] = useState<"transfer" | "new">("new");
  /* Both registration charges, so choosing the tag can put the right one in
     the fee box. A transfer is a different transaction at the tax collector
     from issuing a plate, and costs a fraction as much. */
  const [tagFees, setTagFees] = useState({ new: 0, transfer: 0 });
  const [tagNumber, setTagNumber] = useState("");

  /* The traded car. Kept apart from the fee figures because it becomes a
     vehicle, not a line on the itemization. */
  const [trade, setTrade] = useState({
    vin: "", vinNonStandard: false, year: "", make: "", model: "", trim: "",
    color: "", odometer: "", lienholderName: "", titleState: "FL",
  });
  const [tradeErr, setTradeErr] = useState<"vin" | "other" | null>(null);
  const [tradeMsg, setTradeMsg] = useState<string | null>(null);
  /* null until somebody chooses, which is not the same as 0. Zero IS a
     choice — it is "sold as-is" — and a form that starts on 90 days gets a
     car sold as-is written up with a three-month powertrain warranty on the
     Buyers Guide, which is part of the contract and overrides the purchase
     agreement. The default was doing exactly that. */
  const [warrantyDays, setWarrantyDays] = useState<number | null>(null);

  const [f, setF] = useState({
    price: "", dealerServices: "", efiling: "",
    licenseReg: "", titleFee: "", tempTag: "",
    tradeAllowance: "", tradePayoff: "",
    cashDown: "", deferredDown: "",
    insCompany: "", insPolicy: "", insCode: "",
  });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  /* A car or a buyer chosen counts as work too — picking those is the slow
     part, and losing the selection is as annoying as losing the figures.
     The fee fields are prefilled from settings, so they are not compared. */
  const [saved, setSaved] = useState(false);

  useUnsavedGuard(
    !saved && (Boolean(vehicleId) || Boolean(buyerId)
      || Boolean(f.price || f.tradeAllowance || f.tradePayoff
                 || f.cashDown || f.deferredDown
                 || f.insCompany || f.insPolicy || f.insCode)),
    "this deal");

  async function reload() {
    const [v, c] = await Promise.all([
      supabase.from("vehicles")
        .select("id,stock_no,year,make,model,trim_level,odometer,asking_price_cents,vin")
        .in("status", ["available", "on_hold"]).order("created_at", { ascending: false }),
      supabase.from("customers")
        .select("id,first_name,middle_name,last_name,county,state,phone_cell,email,is_business").order("last_name"),
    ]);
    if (v.data) setVehicles(v.data as VehicleOpt[]);
    if (c.data) setCustomers(c.data as CustomerOpt[]);
    return { vehicles: (v.data ?? []) as VehicleOpt[], customers: (c.data ?? []) as CustomerOpt[] };
  }

  useEffect(() => {
    Promise.all([
      supabase.from("vehicles")
        .select("id,stock_no,year,make,model,trim_level,odometer,asking_price_cents,vin")
        .in("status", ["available", "on_hold"]).order("created_at", { ascending: false }),
      supabase.from("customers")
        .select("id,first_name,middle_name,last_name,county,state,phone_cell,email,is_business").order("last_name"),
      supabase.from("dealer_settings").select("*").maybeSingle(),
      loadCounties(),
    ]).then(([v, c, s, co]) => {
      if (v.error || c.error) { setLoadErr("Could not load vehicles or customers."); return; }
      setVehicles((v.data ?? []) as VehicleOpt[]);
      setCustomers((c.data ?? []) as CustomerOpt[]);
      setCounties(co);
      if (s.data) {
        setTagFees({
          new: s.data.default_license_reg_cents ?? 0,
          transfer: s.data.default_tag_transfer_cents ?? 0,
        });
        setF((x) => ({
          ...x,
          dealerServices: centsToInput(s.data.default_predelivery_service_cents ?? 0),
          efiling: centsToInput(s.data.default_electronic_filing_cents ?? 0),
          licenseReg: centsToInput(s.data.default_license_reg_cents ?? 0),
          titleFee: centsToInput(s.data.default_title_cents ?? 0),
          tempTag: centsToInput(s.data.default_temp_tag_cents ?? 0),
        }));
      }
    }).catch(() => setLoadErr("Could not load the deal screen."));
  }, []);

  /* Picking the car fills the price; picking the buyer fills the county,
     because the surtax follows where the buyer lives, not where the lot is. */
  function pickVehicleFrom(v: VehicleOpt) {
    setVehicleId(v.id);
    setF((s) => (s.price ? s : { ...s, price: centsToInput(v.asking_price_cents) }));
  }
  function pickVehicle(id: string) {
    setVehicleId(id);
    const v = vehicles.find((x) => x.id === id);
    if (v && !f.price) setF((s) => ({ ...s, price: centsToInput(v.asking_price_cents) }));
  }
  function pickBuyer(id: string) {
    setBuyerId(id);
    const c = customers.find((x) => x.id === id);
    if (c?.county) setCounty(c.county);
  }

  const surtaxBps = counties.find((c) => c.name === county)?.surtax_bps ?? 0;

  const amounts: DealAmounts = useMemo(() => ({
    cashPrice: money(f.price),
    predeliveryService: money(f.dealerServices),
    electronicFiling: money(f.efiling),
    licenseRegistration: money(f.licenseReg),
    titleFee: money(f.titleFee),
    tempTag: money(f.tempTag),
    tradeAllowance: money(f.tradeAllowance),
    tradePayoff: money(f.tradePayoff),
    cashDown: money(f.cashDown),
    deferredDown: money(f.deferredDown),
    countySurtaxBps: surtaxBps,
  }), [f, surtaxBps]);

  const t = computeDeal(amounts);

  /* If the cash down does not cover the balance, the remainder is a deferred
     down — a pickup payment — rather than an open balance nobody agreed to. */
  /* These two must sit above the early returns below. React counts hooks per
     render, so a useMemo placed after "if (making === ...) return" ran on
     some renders and not others — which is React error #310 and a blank
     screen when the New deal page opened before its lists had loaded. */
  /* Sorted by make, then model, then year — the lot read as a lot rather than
     in whatever order the rows came back. The VIN and phone go in `extra` so
     they can be searched without cluttering every line. */
  const buyerIsBusiness = Boolean(
    customers.find((c) => c.id === buyerId)?.is_business);

  const vehicleChoices: Choice[] = useMemo(() =>
    [...vehicles]
      .sort((a, b) =>
        a.make.localeCompare(b.make)
        || a.model.localeCompare(b.model)
        || a.year - b.year)
      .map((v) => ({
        id: v.id,
        label: `${v.year} ${v.make} ${v.model}`
             + `${v.trim_level ? ` ${v.trim_level}` : ""}`
             + ` — ${v.stock_no} — ${v.odometer.toLocaleString("en-US")} mi`,
        extra: v.vin ?? "",
      })), [vehicles]);

  const customerChoices: Choice[] = useMemo(() =>
    [...customers]
      .sort((a, b) => customerName(a).localeCompare(customerName(b)))
      .map((c) => ({
        id: c.id,
        label: `${customerName(c)}${c.county ? ` — ${c.county}` : ""}`,
        extra: `${c.phone_cell ?? ""} ${c.email ?? ""}`,
      })), [customers]);

  const [autoDeferred, setAutoDeferred] = useState(true);
  useEffect(() => {
    if (!autoDeferred) return;
    const base = computeDeal({ ...amounts, deferredDown: 0 });
    const shortfall = Math.max(0, base.unpaidBalance);
    const want = shortfall === 0 ? "" : centsToInput(shortfall);
    setF((s) => (s.deferredDown === want ? s : { ...s, deferredDown: want }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.price, f.dealerServices, f.efiling, f.licenseReg, f.titleFee, f.tempTag,
      f.tradeAllowance, f.tradePayoff, f.cashDown, surtaxBps, autoDeferred]);

  const problems: string[] = [];
  if (!vehicleId) problems.push("Pick a vehicle.");
  if (!buyerId) problems.push("Pick a buyer.");
  if (!county) problems.push("Pick the buyer's county.");
  if (amounts.cashPrice <= 0) problems.push("Enter a cash price.");
  if (warrantyDays === null) problems.push("Choose the warranty, or Sold as-is.");
  if (amounts.tradeAllowance >
      amounts.cashPrice + amounts.predeliveryService + amounts.electronicFiling)
    problems.push("The trade allowance is more than the price. Florida does not refund tax on trade equity.");

  async function save() {
    if (problems.length) return;

    const created: { id?: string } = {};
    const ok = await save_.run(async () => {
      const { data, error } = await supabase.from("deals").insert({
        vehicle_id: vehicleId,
        buyer_id: buyerId,
        co_buyer_id: coBuyerId || null,
        joint_ownership: coBuyerId ? joint : null,
        county_name: county,
        county_surtax_bps: surtaxBps,
        state_tax_bps: 600,
        cash_price_cents: amounts.cashPrice,
        predelivery_service_cents: amounts.predeliveryService,
        electronic_filing_cents: amounts.electronicFiling,
        license_registration_cents: amounts.licenseRegistration,
        tag_choice: tagChoice,
        tag_number: tagChoice === "transfer" ? (tagNumber || null) : null,
        title_fee_cents: amounts.titleFee,
        temp_tag_cents: amounts.tempTag,
        trade_allowance_cents: amounts.tradeAllowance,
        trade_payoff_cents: amounts.tradePayoff,
        cash_down_cents: amounts.cashDown,
        deferred_down_cents: amounts.deferredDown,
        insurance_company: f.insCompany.trim() || null,
        insurance_policy_no: f.insPolicy.trim() || null,
        insurance_company_code: f.insCode.trim() || null,
        warranty_days: warrantyDays ?? 0,
        warranty_miles: termFor(warrantyDays ?? 0).miles,
        status: "quote",
      }).select("id,deal_no").single();

      if (error?.message.includes("deals_one_active_per_vehicle")) {
        throw new SaveFailure("That vehicle already has an open deal.");
      }
      created.id = (mustSave({ data, error }) as { id: string }).id;
    }, { toast: "Deal saved" });
    if (!ok || !created.id) return;
    const dealId = created.id;
    /* The deal exists. Now the traded car, if there was one — written after
       the deal because it hangs off it, and reported rather than swallowed:
       a trade that failed to reach inventory is a car sitting outside on the
       lot with no record of it. */
    const allowance = parseMoney(f.tradeAllowance) ?? 0;
    if (allowance > 0) {
      try {
        const buyer = customers.find((c) => c.id === buyerId);
        const { stockNo } = await recordTrade(dealId, {
          vin: trade.vin,
          vinNonStandard: trade.vinNonStandard,
          year: Number(trade.year),
          make: trade.make,
          model: trade.model,
          trim: trade.trim,
          color: trade.color,
          odometer: Number(trade.odometer),
          allowanceCents: allowance,
          payoffCents: parseMoney(f.tradePayoff) ?? 0,
          lienholderName: trade.lienholderName,
          titleState: trade.titleState,
        }, buyer ? customerName(buyer) : "Trade-in");
        setTradeMsg(`Trade added to inventory as stock ${stockNo}.`);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Could not record the trade.";
        setTradeErr(msg.toLowerCase().includes("vin") ? "vin" : "other");
        setTradeMsg(`The deal was saved, but the trade was not added to `
                  + `inventory: ${msg} Add it from the Vehicles page.`);
        return;                       // stay here so the message is read
      }
    }

    setSaved(true);
    clearUnsavedWork();
    onDone(dealId);
  }

  if (making === "vehicle") {
    return (
      <AddVehicle
        onCancel={() => setMaking(null)}
        onDone={async () => {
          const { vehicles: vs } = await reload();
          const newest = vs[0];
          if (newest) pickVehicleFrom(newest);
          setMaking(null);
        }}
      />
    );
  }
  if (making === "customer") {
    return (
      <AddCustomer
        onCancel={() => setMaking(null)}
        onDone={async (newId) => {
          const { customers: cs } = await reload();
          const made = cs.find((c) => c.id === newId);
          setBuyerId(newId);
          if (made?.county) setCounty(made.county);
          setMaking(null);
        }}
      />
    );
  }

  if (loadErr) return <ErrorNote>{loadErr}</ErrorNote>;

  if (vehicles.length === 0 || customers.length === 0) {
    return (
      <>
        <div className="head"><h2>New deal</h2></div>
        <Empty>
          {vehicles.length === 0
            ? "No vehicles available to sell. Add one to the lot first."
            : "No customers yet. Add the buyer before writing the deal."}
        </Empty>
        <div className="actions">
          <Button variant="ghost" onClick={onCancel}>Back</Button>
        </div>
      </>
    );
  }

  const line = (label: string, cents: number, cls = "") => (
    <div className={`li ${cls}`} key={label}>
      <span>{label}</span><span className="money">{formatMoney(cents)}</span>
    </div>
  );

  return (
    <>
      <div className="head"><h2>New deal</h2></div>

      <SearchSelect
        label="Vehicle" value={vehicleId} placeholder="Choose a car"
        action={{ id: "__new", label: "+ Add a vehicle" }}
        onChange={(v) => (v === "__new" ? setMaking("vehicle") : pickVehicle(v))}
        choices={vehicleChoices}
        hint="Type a make, model, year, stock number or the last of the VIN" />

      <SearchSelect
        label="Buyer" value={buyerId} placeholder="Choose a customer"
        action={{ id: "__new", label: "+ Add a customer" }}
        onChange={(v) => (v === "__new" ? setMaking("customer") : pickBuyer(v))}
        choices={customerChoices}
        hint="Type a name, or a phone number" />

      {/* A second buyer, and how the two hold the title. Hidden for a
          business, where the company is the owner and the person named is
          only the officer who signs for it. */}
      {!buyerIsBusiness && (
        <>
          <SearchSelect
            label="Second buyer" value={coBuyerId} placeholder="Nobody — one buyer"
            action={{ id: "", label: "Nobody — one buyer" }}
            onChange={(v) => {
              setCoBuyerId(v === buyerId ? "" : v);
              if (v && !joint) setJoint("and");
            }}
            choices={customerChoices.filter((c) => c.id !== buyerId)}
            hint="Both names go on the title" />

          {coBuyerId && (
            <Select label="How they hold it" value={joint}
                    onChange={(v) => setJoint(v as "and" | "or")}
                    hint={joint === "or"
                      ? "Either one can sell the car on their own"
                      : "Both must sign to sell the car"}>
              <option value="and">AND — both must sign to sell</option>
              <option value="or">OR — either can sell alone</option>
            </Select>
          )}
        </>
      )}

      <Select label="Buyer's county" value={county} onChange={setCounty}
              placeholder="Choose"
              hint="Sets the surtax. Taken from the buyer's address.">
        {counties.map((c) => (
          <option key={c.name} value={c.name}>{c.name} ({surtaxLabel(c.surtax_bps)})</option>
        ))}
      </Select>

      <Panel title="Insurance" rail="record"
             note="Printed on the Florida affidavit">
      <p className="sub">
        Coverage on this vehicle, taken from the customer's card.
      </p>
      <div className="grid2">
        <TextField label="Insurance company" value={f.insCompany} onChange={set("insCompany")} />
        <TextField label="Policy number" value={f.insPolicy} onChange={set("insPolicy")} />
      </div>
      <InsuranceCodeField value={f.insCode} onChange={set("insCode")} />
      </Panel>

      <Panel title="Warranty" rail="record">
      <Select label="In-house warranty"
              value={warrantyDays === null ? "" : String(warrantyDays)}
              onChange={(v) => setWarrantyDays(v === "" ? null : Number(v))}
              placeholder="Choose one"
              hint={
                /* No red until something is actually wrong: everywhere else in
                   this form "not filled in yet" is the problems list at the
                   bottom, which blocks Save and names this. */
                warrantyDays === null
                  ? "Required. Whatever is picked here is what the Buyers Guide prints, "
                    + "and the guide overrides the purchase agreement."
                  : warrantyDays
                    ? `Powertrain, ${termFor(warrantyDays).printed}. The Buyers Guide will say so.`
                    : "The Buyers Guide will show AS IS — no dealer warranty."
              }>
        {WARRANTY_TERMS.map((t) => (
          <option key={t.days} value={t.days}>{t.label}</option>
        ))}
      </Select>
      </Panel>

      <Panel title="Price and fees" rail="money">
      <MoneyField label="Cash price" value={f.price} onChange={set("price")} />
      <div className="grid2">
        <MoneyField label="Dealer services" value={f.dealerServices}
                    onChange={set("dealerServices")} hint="Taxable" />
        <MoneyField label="Electronic filing" value={f.efiling}
                    onChange={set("efiling")} hint="Taxable" />
      </div>
      {/* Asked before the fee, because the answer decides the fee. */}
      <div className="grid2">
        <Select label="Tag" value={tagChoice}
                onChange={(v) => {
                  const choice = v as "transfer" | "new";
                  setTagChoice(choice);
                  setF((s) => ({
                    ...s,
                    licenseReg: centsToInput(
                      choice === "transfer" ? tagFees.transfer : tagFees.new),
                  }));
                }}>
          <option value="new">New tag</option>
          <option value="transfer">Transferring a tag</option>
        </Select>
        {tagChoice === "transfer" && (
          <TagField label="Tag number" value={tagNumber} onChange={setTagNumber}
                 hint="Leave blank if they do not have it — the title
                       application prints Tag Credit instead" />
        )}
      </div>

      <div className="grid2">
        <MoneyField label="License and registration" value={f.licenseReg}
                    onChange={set("licenseReg")}
                    hint={tagChoice === "transfer"
                      ? "Not taxable — transferring their plate"
                      : "Not taxable — issuing a new plate"} />
        <MoneyField label="Title fee" value={f.titleFee}
                    onChange={set("titleFee")} hint="Not taxable" />
      </div>
      <MoneyField label="Temp tag" value={f.tempTag} onChange={set("tempTag")}
                  hint="Not taxable at cost" />

      <details className="more" open={Boolean(f.tradeAllowance)}>
        <summary>Trade-in</summary>
        <div className="grid2">
          <MoneyField label="Trade allowance" value={f.tradeAllowance}
                      onChange={set("tradeAllowance")} hint="Reduces the taxable amount" />
          <MoneyField label="Trade payoff" value={f.tradePayoff}
                      onChange={set("tradePayoff")} hint="Added after tax" />
        </div>

        {/* The car itself. Taking a trade without recording what it is leaves
            a vehicle on the lot that no record accounts for — so once there
            is an allowance, these are required. */}
        {Boolean(parseMoney(f.tradeAllowance)) && (
          <>
            <p className="taxnote">
              This car goes on the lot when the deal is written, priced at what
              you allowed for it. It will need a look over and an asking price.
            </p>
            <Field label="VIN" mono maxLength={17} autoCapitalize="characters"
                   spellCheck={false} autoComplete="off"
                   value={trade.vin}
                   onChange={(v: string) => setTrade((s) => ({ ...s, vin: v.toUpperCase() }))}
                   error={tradeErr === "vin" ? tradeMsg : null} />
            <label className="checkline">
              <input type="checkbox" checked={trade.vinNonStandard}
                     onChange={(e) => setTrade((s) => ({ ...s, vinNonStandard: e.target.checked }))} />
              Built before 1981
            </label>
            <div className="grid2">
              <TextField label="Year" value={trade.year}
                         onChange={(v) => setTrade((s) => ({ ...s, year: v.replace(/\D/g, "").slice(0, 4) }))} />
              <TextField label="Make" value={trade.make}
                         onChange={(v) => setTrade((s) => ({ ...s, make: v }))} />
            </div>
            <div className="grid2">
              <TextField label="Model" value={trade.model}
                         onChange={(v) => setTrade((s) => ({ ...s, model: v }))} />
              <TextField label="Trim" value={trade.trim}
                         onChange={(v) => setTrade((s) => ({ ...s, trim: v }))} />
            </div>
            <div className="grid2">
              <OdometerField value={trade.odometer}
                             onChange={(v) => setTrade((s) => ({ ...s, odometer: v }))} />
              <OptionSelect label="Color" value={trade.color}
                            onChange={(v) => setTrade((s) => ({ ...s, color: v }))}
                            options={COLORS} />
            </div>
            <div className="grid2">
              <TextField label="Lienholder" value={trade.lienholderName}
                         onChange={(v) => setTrade((s) => ({ ...s, lienholderName: v }))}
                         hint="If there is a payoff" />
              <StateSelect label="Title state" value={trade.titleState}
                           onChange={(v) => setTrade((s) => ({ ...s, titleState: v }))} />
            </div>
            {tradeErr === "other" && tradeMsg && <ErrorNote>{tradeMsg}</ErrorNote>}
          </>
        )}
      </details>
      </Panel>

      <Panel title="Itemization" rail="money"
             figure={formatMoney(t.totalAmountDue)}>
      <div className="item">
        {line("Cash price", amounts.cashPrice)}
        {amounts.predeliveryService > 0 && line("Dealer services", amounts.predeliveryService)}
        {amounts.electronicFiling > 0 && line("Electronic filing", amounts.electronicFiling)}
        {amounts.tradeAllowance > 0 && line("Less trade allowance", -amounts.tradeAllowance, "neg")}
        {line("Taxable amount", t.taxableAmount, "total")}
        {line("Sales tax (6%)", t.stateTax)}
        {line(`Surtax (${bpsToPercent(surtaxBps)})`, t.surtax)}
        {line("Total sales price", t.totalSalesPrice, "total")}
        {amounts.licenseRegistration > 0 && line("License and registration", amounts.licenseRegistration)}
        {amounts.titleFee > 0 && line("Title fee", amounts.titleFee)}
        {amounts.tempTag > 0 && line("Temp tag", amounts.tempTag)}
        {amounts.tradePayoff > 0 && line("Plus trade payoff", amounts.tradePayoff)}
        {line("Total due", t.totalAmountDue, "total")}
      </div>
      <p className="taxnote">
        {surtaxBps === 0
          ? `${county || "This county"} charges no discretionary surtax.`
          : `Surtax applies to the first $5,000${t.surtaxWasCapped ? ", so it is capped here" : ""}.`}
      </p>
      </Panel>

      <Panel title="Down payment" rail="keep">
      <div className="grid2">
        <MoneyField label="Cash down" value={f.cashDown} onChange={set("cashDown")} />
        <MoneyField label="Deferred down" value={f.deferredDown}
                    onChange={(v) => { setAutoDeferred(false); set("deferredDown")(v); }}
                    hint={autoDeferred ? "Filled with whatever cash down leaves" : "Pickup payment"} />
      </div>
      {!autoDeferred && (
        <p className="hint">
          <button className="linkbtn" onClick={() => setAutoDeferred(true)}>
            Go back to filling this automatically
          </button>
        </p>
      )}

      <div className="item">
        {amounts.cashDown > 0 && line("Less cash down", -amounts.cashDown, "neg")}
        {amounts.deferredDown > 0 && line("Less deferred down", -amounts.deferredDown, "neg")}
        {line("Unpaid balance", t.unpaidBalance, "due")}
      </div>
      </Panel>

      {problems.length > 0 && (
        <ul className="problems">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      <SaveBar save={save_} onSave={() => void save()} label="Save deal"
               alwaysPrimary idle="Not saved yet" disabled={problems.length > 0}>
        <Button variant="ghost" onClick={onCancel} disabled={save_.saving}>Cancel</Button>
      </SaveBar>
    </>
  );
}
